import { fromJS, Map, List } from 'immutable';
import { authUserIdSelector } from 'selectors/authSelectors';
import { sortDate } from 'helpers/utilHelpers';

import {
  getAllUsers,
  getUsers,
  getUsersAndClients,
  getUsersWithClients,
  getUsersWithDepartment,
} from 'selectors/entities/userSelectors';
import { getClients } from './clientsSelectors';
import { getServices } from './servicesSelectors';
import { getTasksWithSummary, getTaskLabels, getTaskStatuses } from './taskSelectors';
import { getProjectListDataset } from '../ui/projectListSelectors';
import { getClientProfiles } from 'selectors/entities/userSelectors';

import { getUserFullName, getUserShortName } from 'helpers/userHelper';
import moment from 'moment';
import { backendDateFormat } from 'util/config';
import { getFiles } from 'selectors/entities/fileSelector';
import { sortString } from 'helpers/utilHelpers';
import { createImmutableSelector } from 'util/reselectUtils';

// Returns all projects from state
export const getProjects = (state) => state.getIn(['entities', 'project', 'projects']);

// Returns all projects sorted by name
export const getProjectsSortedByName = createImmutableSelector([getProjects], (projects) =>
  projects.sort((a, b) => sortString(a.get('name'), b.get('name'))),
);

// Returns favorites of projects
export const getProjectFavorites = (state) => state.getIn(['entities', 'project', 'projectsFavoritesMap']);

// Return all services map state
export const getServicesMap = (state) => state.getIn(['entities', 'project', 'projectsServicesMap']);

// Return all service history state
export const getServiceHistory = (state) => state.getIn(['entities', 'project', 'projectServiceHistory']);

// Return team maps
export const getTeamMaps = (state) => state.getIn(['entities', 'project', 'teamMaps']);

// Returns drive folders list from state
export const getDriveFolders = (state) => state.getIn(['entities', 'project', 'driveFolders']);

// Returns status of project drive synchronization
export const getDriveSyncing = (state) => state.getIn(['entities', 'project', 'isDriveSyncing']);

// Returns projects important pages from state
export const getProjectImportantPages = (state, { projectId }) => {
  let pages = state.getIn(['entities', 'project', 'projectsImportantPages']);
  if (pages && projectId) {
    pages = pages.filter((page) => page.get('projectId') === projectId);
  }
  return pages;
};

export const getFeedByProjectId = (state, { projectId }) =>
  state
    .getIn(['ui', 'notificationDrawer', 'notifications'])
    .filter((notification) => notification.getIn(['relatedEntity', 'model', 'projectId']) === projectId);

// Returns all active services of specified user
export const getServicesMapByUserId = (state, { userId }) =>
  state
    .getIn(['entities', 'project', 'projectsServicesMap'])
    .filter((service) => service.get('status') === 'active')
    .filter((service) => service.get('userId') === userId);

// Return all service map by project id and end date is null
export const getServiceByProjectId = (state, { projectId, status }) =>
  state
    .getIn(['entities', 'project', 'projectsServicesMap'])
    .filter((map) => map.get('projectId') === projectId)
    .filter((map) => (status ? status === map.get('status') : true))
    .map((service) =>
      service.merge({
        service: getServices(state).get(service.get('serviceId')),
      }),
    );

// Returns all projects filtered by current project list dataset
export const getProjectsByDataset = createImmutableSelector(
  [getProjects, getProjectListDataset],
  (projects, dataset) => {
    return projects
      .filter((project) => dataset.indexOf(project && project.get('projectId')) !== -1)
      .sort((a, b) => {
        const aIndex = dataset.indexOf(a.get('projectId'));
        const bIndex = dataset.indexOf(b.get('projectId'));

        return aIndex > bIndex ? 1 : -1;
      });
  },
);

export const getProjectById = (state, { projectId }) =>
  state.getIn(['entities', 'project', 'projects', parseInt(projectId)]);

export const getProjectsByUserId = (state, { userId, exceptProjectId }) =>
  state
    .getIn(['entities', 'project', 'projects'])
    .filter(
      (project) =>
        project.get('userId') === userId && (!exceptProjectId || project.get('projectId') !== exceptProjectId),
    );

export const getTeamByProjectId = createImmutableSelector(
  [getProjectById, getTeamMaps, getUsersAndClients],
  (project, teamMaps, users) => {
    return project
      ?.get('team')
      ?.map((mapId) => users.get(teamMaps.getIn([mapId, 'userId'])))
      .filter((user) => user);
  },
);

export const getServiceMapById = (state, { relationId }) =>
  state.getIn(['entities', 'project', 'projectsServicesMap', relationId]);

// Return service map by id with related models info
export const getServiceMapByIdWithSummary = createImmutableSelector(
  [getServiceMapById, getAllUsers, getServices],
  (serviceMap, users, services) => {
    if (serviceMap) {
      const user = users.get(serviceMap.get('userId'));
      const leader = users.get(serviceMap.get('leaderId'));
      const service = services.get(serviceMap.get('serviceId'));
      return serviceMap.merge(
        fromJS({
          userFullName: user ? getUserFullName(user) : null,
          leaderFullName: leader ? getUserFullName(leader) : null,
          service: service ? service : null,
        }),
      );
    }
    return null;
  },
);

// Returns all active projects from state
export const getActiveProjects = createImmutableSelector([getProjects, getServicesMap], (projects, serviceMaps) => {
  return projects
    .filter((project) => project.get('status') === 'active')
    .map((project) => {
      return project.set(
        'services',
        project.get('services')?.map((id) => serviceMaps.get(id)),
      );
    });
});

// Returns project by id with related models info
export const getProjectByIdWithSummary = createImmutableSelector(
  [
    getProjectById,
    getClients,
    getServicesMap,
    getServices,
    getUsers,
    getTasksWithSummary,
    getTeamMaps,
    getClientProfiles,
    getTaskLabels,
    getTaskStatuses,
    getFiles,
  ],
  (
    project,
    clients,
    serviceMaps,
    services,
    users,
    tasks,
    teamMap,
    clientProfiles,
    taskLabels,
    taskStatuses,
    allFiles,
  ) => {
    if (!project) {
      return null;
    }
    const client = clients.get(project.get('clientId'));
    let projectServiceMaps,
      projectTasks,
      team,
      projectClientProfiles,
      lastTasks,
      files = [];

    if (services && serviceMaps && project.get('services')) {
      projectServiceMaps = project
        .get('services')
        .map((id) => serviceMaps.get(id))
        .map((map) => map.set('service', services.get(map.get('serviceId'))));
    }

    if (teamMap && project.get('team')) {
      team = project
        .get('team')
        .map((id) => teamMap.get(id))
        .map((map) => {
          if (map) {
            if (map.get('type') !== 'client') {
              return map.set('user', users.get(map.get('userId')));
            } else {
              return map.set('user', clientProfiles.get(map.get('userId')));
            }
          }
          return null;
        })
        .filter((map) => map && map.get('user'));

      if (projectServiceMaps) {
        team = team.map((teamMap) => {
          let canDelete = false;
          if (teamMap.get('type') === 'leader') {
            canDelete = !projectServiceMaps.find(
              (map) => map.get('leaderId') === teamMap.get('userId') && map.get('status') === 'active',
            );
          }

          if (teamMap.get('type') === 'responsible') {
            canDelete = !projectServiceMaps.find(
              (map) => map.get('userId') === teamMap.get('userId') && map.get('status') === 'active',
            );
          }

          if (teamMap.get('type') === 'participant') {
            canDelete = true;
          }

          return teamMap.set('canDelete', canDelete);
        });
      }
    }

    if (tasks) {
      projectTasks = tasks.map((task) => {
        const user = users.get(task.get('userId'));
        return task.set('user', user);
      });
    }

    if (project.get('clientProfiles')) {
      projectClientProfiles = project.get('clientProfiles').map((id) => clientProfiles.get(id));
    }

    if (allFiles && project.get('files')?.size) {
      files = project
        .get('files')
        .map((fileId) => allFiles.get(fileId))
        .filter((file) => file);
    }

    if (project.get('lastTasks') && taskLabels) {
      lastTasks = project.get('lastTasks').map((task) =>
        task
          // labels могут не быть если задача пришла из сокетов,
          // поэтому используем List как дефолтное значение
          .set('labels', task.get('labels')?.map((id) => taskLabels.get(id)) ?? Map())
          .set('taskStatus', taskStatuses.get(task.get('statusId'))),
      );
    }

    return project.merge(
      fromJS({
        services: projectServiceMaps,
        tasks: projectTasks,
        team,
        client,
        lastTasks,
        clientProfiles: projectClientProfiles,
        files,
      }),
    );
  },
);

// Returns project with related models info
export const getProjectsWithSummary = createImmutableSelector(
  [getProjectsByDataset, getClients, getServicesMap, getServices, getUsers, getTeamMaps, getClientProfiles],
  (projects, clients, serviceMap, services, users, teamMap, clientProfiles) => {
    return projects.map((project) => {
      const projectClient = clients.get(project.get('clientId'));
      let team, manager, projectServiceMaps;

      if (teamMap && project.get('team')) {
        team = project
          .get('team')
          .map((id) => teamMap.get(id))
          .map((map) => {
            if (map) {
              if (map.get('type') !== 'client') {
                return map.set('user', users.get(map.get('userId')));
              } else {
                return map.set('user', clientProfiles.get(map.get('userId')));
              }
            }

            return null;
          })
          .filter((map) => map && map.get('user'));

        manager = users.get(project.get('userId'));
      }

      if (services && serviceMap && project.get('services')) {
        projectServiceMaps = project
          .get('services')
          .map((id) => serviceMap.get(id))
          .map((map) => map.set('service', services.get(map.get('serviceId'))));
      }

      return project.merge(
        fromJS({
          mangerName: getUserShortName(manager),
          clientFullName: getUserFullName(projectClient),
          services: projectServiceMaps,
          team,
        }),
      );
    });
  },
);

const prepareProjectLeaderWithSummary = (
  clients,
  serviceMap,
  services,
  users,
  authUserId,
  teamMap,
  userClientProfiles,
) => (project) => {
  let leader, team, projectServiceMaps;
  const projectClient = clients.get(project.get('clientId'));

  if (teamMap && project.get('team')) {
    team = project
      .get('team')
      .map((id) => teamMap.get(id))
      .map((map) => {
        if (map) {
          if (map.get('type') !== 'client') {
            return map.set('user', users.get(map.get('userId')));
          } else {
            return map.set('user', userClientProfiles.get(map.get('userId')));
          }
        }

        return null;
      })
      .filter((map) => map && map.get('user'));

    leader = team ? team.find((memberTeam) => memberTeam.get('type') === 'leader') : null;
    leader = leader ? users.get(leader.get('userId')) : null;
  }

  if (services && serviceMap && project.get('services')) {
    projectServiceMaps = project
      .get('services')
      .map((id) => serviceMap.get(id))
      .map((map) => map.set('service', services.get(map.get('serviceId'))));
  }
  return project.merge(
    fromJS({
      leaderFullName: getUserFullName(leader),
      clientFullName: getUserFullName(projectClient),
      services: projectServiceMaps,
      team,
    }),
  );
};

// Returns all project with related models info without dataset
export const getAllProjectsLeaderWithSummary = createImmutableSelector(
  [
    getProjects,
    getClients,
    getServicesMap,
    getServices,
    getUsersWithDepartment,
    authUserIdSelector,
    getTeamMaps,
    getClientProfiles,
  ],
  (projects, clients, serviceMap, services, users, authUserId, teamMap, userClientProfiles) => {
    return projects.map(
      prepareProjectLeaderWithSummary(clients, serviceMap, services, users, authUserId, teamMap, userClientProfiles),
    );
  },
);

// Returns all project with related models info without dataset
export const getProjectByIdLeaderWithSummary = createImmutableSelector(
  [
    getProjectById,
    getClients,
    getServicesMap,
    getServices,
    getUsersWithDepartment,
    authUserIdSelector,
    getTeamMaps,
    getClientProfiles,
  ],
  (project, clients, serviceMap, services, users, authUserId, teamMap, userClientProfiles) => {
    if (!project) {
      return null;
    }

    return prepareProjectLeaderWithSummary(
      clients,
      serviceMap,
      services,
      users,
      authUserId,
      teamMap,
      userClientProfiles,
    )(project);
  },
);

// Returns active project with related models info without dataset
export const getProjectsLeaderWithSummary = createImmutableSelector(
  [getAllProjectsLeaderWithSummary, authUserIdSelector],
  (projects, authUserId) => {
    return projects
      .map((project) => {
        if (project.get('status') !== 'active') {
          return null;
        }

        const services = project.get('services')?.filter((service) => {
          const hasLeader = service.get('leaderId') === authUserId;
          const hasActive = service.get('status') === 'active';
          return hasLeader && hasActive;
        });

        if (!services?.size) {
          return null;
        }

        return project.set('services', services);
      })
      .filter((project) => project);
  },
);

// Get projects for user when is manager projects
export const getProjectsByManager = createImmutableSelector(
  [getAllProjectsLeaderWithSummary, authUserIdSelector],
  (projects, authUserId) => {
    return projects.filter((project) => {
      const team = project.get('team');

      return team?.size
        ? !!team.find((memberTeam) => memberTeam.get('userId') === authUserId && memberTeam.get('type') === 'manager')
        : false;
    });
  },
);

// Function returns project model with services and team
export const getProjectByIdWithClientUsers = createImmutableSelector(
  [getProjectById, getUsers, getClientProfiles, getTeamMaps, getServicesMap],
  (project, users, clientProfiles, teamMap, services) => {
    if (project) {
      let team = null;

      if (teamMap && project.get('team')) {
        team = project
          .get('team')
          .map((id) => teamMap.get(id))
          .map((map) => {
            if (map) {
              if (map.get('type') !== 'client') {
                return map.set('user', users.get(map.get('userId')));
              } else {
                return map.set('user', clientProfiles.get(map.get('userId')));
              }
            }

            return null;
          })
          .filter((map) => map && map.get('user'));
      }

      // If manager not active then set to null
      if (project.get('userId') && !users.get(project.get('userId'))) {
        project = project.set('userId', null);
      }

      project = project
        .set('team', team)
        .set('services', project.get('services') && project.get('services').map((id) => services.get(id)));
    }
    return project;
  },
);

// Returned projects by user id
export const getProjectsWithProblems = (state) => {
  let projects = state.getIn(['entities', 'project', 'projects']);
  if (projects) {
    projects = projects.filter((project) => project.get('problemsCount') > 0 && project.get('status') === 'active');
  }

  return projects;
};

// Returned projects by user id
export const getProjectsWithMonitoringProblems = createImmutableSelector([getProjects], (projects) => {
  if (!projects) {
    return null;
  }

  return projects.filter((project) => {
    const statistic = project?.get('monitoringInfo');
    if (!statistic) {
      return false;
    }

    const statusCheck = [statistic.get('siteStatus'), statistic.get('siteMirrorStatus')].includes(0);
    const safeCheck = statistic.get('safeProblemCount') > 0;
    const domainCheck =
      statistic.get('domainEndDate') &&
      moment(statistic.get('domainEndDate'), backendDateFormat).isBefore(moment().add(30, 'days'));
    const sslCheck =
      statistic.get('sslEndDate') &&
      moment(statistic.get('sslEndDate'), backendDateFormat).isBefore(moment().add(30, 'days'));

    return statusCheck || safeCheck || domainCheck || sslCheck;
  });
});

export const getProjectFavoritesWithSummary = createImmutableSelector(
  [getProjects, getProjectFavorites, getAllUsers, getClientProfiles],
  (projects, favorites, users, clients) => {
    let result = favorites.map((favorite) =>
      favorite.set('projectName', projects.getIn([favorite.get('projectId'), 'name'])),
    );

    result.forEach((map) => {
      if (map.get('entity') === 'comment' && result.getIn([map.get('mapId'), 'relatedEntity'])) {
        result = result.setIn(
          [map.get('mapId'), 'relatedEntity', 'user'],
          users.merge(clients).get(map.getIn(['relatedEntity', 'createdBy'])),
        );
      }
    });

    return result;
  },
);

export const getProjectFeedSummary = createImmutableSelector(
  [getFeedByProjectId, getUsers, getClientProfiles],
  (notifications, users, clientProfiles) => {
    let byDate = new Map();

    if (notifications && notifications.size) {
      notifications
        .sort((first, second) => {
          return -sortDate(first.get('timeCreated'), second.get('timeCreated'), 'YYYY-MM-DD HH:mm:ss');
        })
        .forEach((notification) => {
          let date = notification
            .get('timeCreated')
            .split(' ')[0]
            .split('-');
          date = date[0] + '-' + date[1];
          let user = users.find((user) => user.get('userId') === notification.getIn(['content', 'userId']));
          if (!user) {
            user = clientProfiles.get(notification.getIn(['content', 'userId']));
          }
          let list = byDate.get(date);
          if (!list) {
            list = new List();
          }

          byDate = byDate.set(date, list.push(notification.set('user', user)));
        });
    }

    return byDate;
  },
);

// Prepare googleSearch data. Use in project-google-search page.
export const getProjectWithPrepareGoogleSearch = (state, { projectId }) => {
  const project = getProjectById(state, { projectId });

  if (!project || !project.get('projectGoogleSearch')) {
    return project;
  }

  const projectGoogleSearch = project.get('projectGoogleSearch');
  let prepareGoogleSearch = new Map();

  ['web', 'image', 'video', 'news'].forEach((searchType) => {
    ['query', 'page', 'country', 'device', 'searchAppearance', 'date'].forEach((dataKey) => {
      prepareGoogleSearch = prepareGoogleSearch.setIn(
        [searchType, dataKey],
        projectGoogleSearch.get(searchType + (dataKey.charAt(0).toUpperCase() + dataKey.slice(1))),
      );
    });

    let charts = new List();
    if (projectGoogleSearch.getIn([searchType + 'Date'])) {
      projectGoogleSearch.getIn([searchType + 'Date']).forEach((row) => {
        const date = parseInt(
          moment(row.get('value'), backendDateFormat)
            .add(3, 'hours')
            .format('x'),
        );
        charts = charts.push({
          date,
          clicks: row.get('clicks'),
          impressions: row.get('impressions'),
          ctr: parseFloat((row.get('ctr') * 100).toFixed(2)),
          position: parseFloat(row.get('position').toFixed(2)),
        });
      });
    }

    prepareGoogleSearch = prepareGoogleSearch.set(
      searchType,
      prepareGoogleSearch.get(searchType).merge(
        fromJS({
          charts,
          timeUpdate: projectGoogleSearch.get('timeUpdated'),
        }),
      ),
    );
  });

  return project.set('projectGoogleSearch', prepareGoogleSearch);
};

// Returned projects with team users
export const getProjectsWithTeam = createImmutableSelector(
  [getProjects, getTeamMaps, getUsersAndClients],
  (projects, teamMaps, users) =>
    projects.map((project) => {
      if (!project.get('team')) {
        return project;
      }

      return project.set(
        'team',
        project.get('team').map((mapId) => users.get(teamMaps.getIn([mapId, 'userId']))?.filter((user) => user)),
      );
    }),
);

// Return project by id with team
export const getProjectByIdWithTeam = createImmutableSelector(
  [getProjectById, getTeamMaps, getUsersWithClients],
  (project, teamMaps, users) => {
    if (!project) {
      return null;
    }

    const team = teamMaps
      .filter((teamMap) => teamMap.get('projectId') === project.get('projectId'))
      .map((teamMap) => teamMap.set('user', users.get(teamMap.get('userId'))))
      .filter((teamMap) => teamMap.get('user'));

    return project.set('team', team.toList());
  },
);
